import { Injectable } from '@angular/core';
import { IRequestCallback, RequestService } from '@df/api/request/request.service';
import { App } from '@df/core/app';
import { Status } from '@df/core/status';
import { StoreCredit } from './store-credit.namespace';

declare module '@tomandco/nexus-api-types' {
    namespace Nexus.Lib.General {
        interface AppData {
            storecreditEnabled?: boolean;
        }
    }
}

@Injectable({
    providedIn: 'root'
})
export class StoreCreditService {
    constructor(
        protected request: RequestService,
        protected app: App
    ) {}

    get enabled(): boolean {
        return !!this.app.data?.storecreditEnabled;
    }

    get balance(): number {
        return this.app.customer.model.data.store_credit_balance || 0;
    }

    get canBeUsed(): boolean {
        return !!(this.enabled && this.app.customer.model.isLoggedIn && this.balance);
    }

    get welcomeOffer(): boolean {
        return !!this.app.customer.model.data.store_credit_welcome_offer;
    }

    get isAdded(): boolean {
        return !!this.app.customer.basket.totals?.credits;
    }

    get history(): StoreCredit.IHistoryItem[] {
        return this.app.customer.model.data.store_credit_history || [];
    }

    get displayAccount(): boolean {
        return !!(this.enabled && this.app.customer.model.isLoggedIn);
    }

    /**
     * Merge gift card balance to store credits balance
     */
    public mergeCard(form, status: Status, data: StoreCredit.IMergeCardData, onSuccess?: IRequestCallback, onError?: IRequestCallback) {
        return this.request.submitForm(form, status, { path: StoreCredit.Endpoints.MergeGiftcard, data }, onSuccess, onError);
    }

    /**
     * Sends email with store credits barcode, balance, code and pin
     */
    public sendCreditsToEmail(status: Status, onSuccess?: IRequestCallback, onError?: IRequestCallback) {
        return this.request.send(status, { path: StoreCredit.Endpoints.SendEmail }, onSuccess, onError);
    }

    /**
     * Set store credits as payment method
     */
    public applyStoreCredit(status: Status, isCheckout = false, onSuccess?: IRequestCallback, onError?: IRequestCallback) {
        return this.request.send(
            status,
            {
                path: isCheckout ? StoreCredit.Endpoints.CheckoutApply : StoreCredit.Endpoints.Apply
            },
            onSuccess,
            onError
        );
    }

    /**
     * Unset store credits as payment method
     */
    public removeStoreCredit(status: Status, isCheckout = false, onSuccess?: IRequestCallback, onError?: IRequestCallback) {
        return this.request.send(
            status,
            {
                path: isCheckout ? StoreCredit.Endpoints.CheckoutRemove : StoreCredit.Endpoints.Remove
            },
            onSuccess,
            onError
        );
    }
}
